/**
 * InlineCommentPopover component for adding comments and suggestions on text selection.
 * 
 * Features:
 * - Appears when user selects text in the essay
 * - Positioned near the selection
 * - Text input for comment
 * - "Add Comment" button for all approved users
 * - "Suggest Edit" button for editors (shows replacement text input)
 * 
 * Called by:
 * - EssayDetailPage when text is selected
 */
import { useState, useCallback, useEffect } from 'react';
import { Card, Input, Button, Space, Typography, Divider, message } from 'antd';
import { CommentOutlined, EditOutlined, CloseOutlined, SendOutlined } from '@ant-design/icons';
import type { User, CreateCommentRequest, CreateSuggestionRequest } from '@compendium/shared';
import { createComment, createSuggestion } from '../services/api';
import type { TextSelection } from './TextHighlighter';

const { TextArea } = Input;
const { Text } = Typography;

// =============================================================================
// Props Interface
// =============================================================================

interface InlineCommentPopoverProps {
  /** The current text selection */
  selection: TextSelection | null;
  /** Essay slug for API calls */
  essaySlug: string;
  /** Current authenticated user */
  currentUser: User | null;
  /** Callback when popover should close */
  onClose: () => void;
  /** Callback when a comment is created */
  onCommentCreated: () => void;
  /** Callback when a suggestion is created */
  onSuggestionCreated: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * InlineCommentPopover component.
 * Shows a floating card for adding comments or suggestions on selected text.
 */
function InlineCommentPopover({
  selection,
  essaySlug,
  currentUser,
  onClose,
  onCommentCreated,
  onSuggestionCreated,
}: InlineCommentPopoverProps) {
  const [mode, setMode] = useState<'comment' | 'suggestion'>('comment');
  const [commentContent, setCommentContent] = useState('');
  const [suggestedText, setSuggestedText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user can suggest edits (editor or author)
  const canSuggestEdits = currentUser?.role === 'editor' || currentUser?.role === 'author';

  // Reset state when selection changes
  useEffect(() => {
    if (selection) {
      setCommentContent('');
      setSuggestedText(selection.text);
      setMode('comment');
    }
  }, [selection]);

  // Handle comment submission
  const handleSubmitComment = useCallback(async () => {
    if (!selection || !commentContent.trim()) return;

    setIsSubmitting(true);
    try {
      const data: CreateCommentRequest = {
        type: 'inline',
        content: commentContent.trim(),
        selectionStart: selection.start,
        selectionEnd: selection.end,
      };

      await createComment(essaySlug, data);
      message.success('Comment added');
      setCommentContent('');
      onCommentCreated();
      onClose();
    } catch (error) {
      message.error('Failed to add comment');
      console.error('Error creating comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selection, commentContent, essaySlug, onCommentCreated, onClose]);

  // Handle suggestion submission
  const handleSubmitSuggestion = useCallback(async () => {
    if (!selection || !suggestedText) return;

    // Don't submit if suggested text is same as original
    if (suggestedText === selection.text) {
      message.warning('Suggested text is the same as original');
      return;
    }

    setIsSubmitting(true);
    try {
      const data: CreateSuggestionRequest = {
        selectionStart: selection.start,
        selectionEnd: selection.end,
        originalText: selection.text,
        suggestedText: suggestedText,
      };

      await createSuggestion(essaySlug, data);
      message.success('Suggestion created');
      setSuggestedText('');
      onSuggestionCreated();
      onClose();
    } catch (error) {
      message.error('Failed to create suggestion');
      console.error('Error creating suggestion:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selection, suggestedText, essaySlug, onSuggestionCreated, onClose]);

  // Don't render if no selection or no user
  if (!selection || !currentUser) {
    return null;
  }

  // Calculate position - place below the selection
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.max(16, Math.min(selection.rect.left, window.innerWidth - 350)),
    top: Math.min(selection.rect.bottom + 8, window.innerHeight - 300),
    zIndex: 1000,
    width: 320,
    maxWidth: 'calc(100vw - 32px)',
  };

  return (
    <Card
      style={style}
      styles={{
        body: {
          padding: '12px',
          background: '#2d2d2d',
        },
      }}
      bordered={false}
    >
      {/* Header with mode toggle */}
      <div style={{ marginBottom: '12px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              type={mode === 'comment' ? 'primary' : 'text'}
              size="small"
              icon={<CommentOutlined />}
              onClick={() => setMode('comment')}
            >
              Comment
            </Button>
            {canSuggestEdits && (
              <Button
                type={mode === 'suggestion' ? 'primary' : 'text'}
                size="small"
                icon={<EditOutlined />}
                onClick={() => setMode('suggestion')}
              >
                Suggest Edit
              </Button>
            )}
          </Space>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </Space>
      </div>

      {/* Selected text preview */}
      <div
        style={{
          padding: '8px',
          background: '#1a1a1a',
          borderRadius: '4px',
          marginBottom: '12px',
          maxHeight: '60px',
          overflow: 'hidden',
        }}
      >
        <Text
          type="secondary"
          style={{
            fontSize: '12px',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          "{selection.text}"
        </Text>
      </div>

      {/* Comment mode */}
      {mode === 'comment' && (
        <>
          <TextArea
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            placeholder="Add a comment..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ marginBottom: '8px' }}
            autoFocus
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmitComment}
            loading={isSubmitting}
            disabled={!commentContent.trim()}
            block
          >
            Add Comment
          </Button>
        </>
      )}

      {/* Suggestion mode */}
      {mode === 'suggestion' && (
        <>
          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Original text:
            </Text>
            <div
              style={{
                padding: '8px',
                background: 'rgba(255, 107, 107, 0.1)',
                borderRadius: '4px',
                textDecoration: 'line-through',
                color: '#ff6b6b',
                fontSize: '14px',
              }}
            >
              {selection.text}
            </div>
          </div>

          <Divider style={{ margin: '8px 0', borderColor: '#444' }} />

          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Suggested replacement:
            </Text>
            <TextArea
              value={suggestedText}
              onChange={(e) => setSuggestedText(e.target.value)}
              placeholder="Enter suggested text..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{
                background: 'rgba(107, 255, 107, 0.1)',
                borderColor: 'rgba(107, 255, 107, 0.3)',
              }}
              autoFocus
            />
          </div>

          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleSubmitSuggestion}
            loading={isSubmitting}
            disabled={!suggestedText || suggestedText === selection.text}
            block
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            Suggest Edit
          </Button>
        </>
      )}
    </Card>
  );
}

export default InlineCommentPopover;
